import Phaser from "phaser";

/**
 * Cinematic procedural sound layer for the game. Uses the WebAudio API
 * directly to synthesize multi-layer SFX (no asset files required).
 *
 * All hero/element sounds share Rylai's Crystal Maiden template skeleton
 * (premium Dota 2 hero-grade structure), each reskinned with element tones:
 *   Structure: SNAP → DESCENDING tonal ring → BODY sweep → HARD ticks → SUB → TAIL.
 *   • fire     = Lina Dragon Slave (sawtooth fire-roar + ember pops + warm afterglow)
 *   • frost    = Crystal Maiden Frostbite/Crystal Nova (TEMPLATE: glassy descending ring + ice shards)
 *   • bomb     = Earthshaker/Tinker shell (shockwave-tone + debris ticks + mega sub + rolling tail)
 *   • lightning= Zeus arc-bolt (square electric carrier + ozone sizzle + ringing tail)
 *   • arcane   = Death/Mortred cast (ethereal triangle + void body + distant bell tail)
 *   • nature   = Mirana Sacred Arrow (bowstring snap + ring-down + moon ticks + moon ring tail)
 *   • laser    = sci-fi beam (sawtooth pew + plasma sizzle + metallic tail)
 *   • wind     = air gust (triangle air-tone + shhh body + whistle tail)
 *   • dragon   = Jakiro/beast breath (throat growl + breath body + chest sub)
 *
 * Master chain: layers → reverb send → master compressor → destination.
 * The compressor protects against clipping when many towers stack.
 */

/** Tower-element sound categories. */
type SfxCategory = "fire" | "frost" | "bomb" | "lightning" | "arcane" | "nature" | "laser" | "wind" | "dragon" | "storm";

/** Tower id → sfx category. Aligned with each hero's actual ability. */
const TOWER_SFX: Record<string, SfxCategory> = {
  // Base
  fire: "fire",            // Lina — flame
  water: "frost",          // Rylai — Crystal Maiden ice magic
  earth: "bomb",           // Zharvakko — small AOE
  nature: "nature",        // Mirana — arrow
  // Fusion
  steam: "fire",           // Mangix — hot mist
  lava: "bomb",            // Karroch — splash blast
  forest_fire: "lightning",// Sven — Stormbolt (lightning bolt projectile)
  mud: "bomb",             // Raigor — totem AOE smash
  ice: "frost",            // Auroth — snowflake shuriken
  vine: "nature",          // Lesale — thorn whip
  brew: "fire",            // Rattletrap — fire ale splash
  voidmage: "lightning",   // Zeus — chain lightning
  beast: "storm",          // Vrogros — heavy storm (per user request 2026-05-11)
  tide: "bomb",            // Slardar — Ravage AOE smash
  necro: "arcane",         // Rotund'jere — death pulse
  phantom: "arcane",       // Mortred — Coup de Grâce
  luna: "laser",           // Luna — Moon Glaive (buzz laser per user request 2026-05-11)
  tinker: "bomb",          // Boush — heavy bomb lob
};

export class SoundSystem {
  private scene: Phaser.Scene;
  private ctx?: AudioContext;
  private masterGain?: GainNode;
  private compressor?: DynamicsCompressorNode;
  private masterLP?: BiquadFilterNode;
  private masterTilt?: BiquadFilterNode;
  private reverbSend?: GainNode;
  private muted = false;
  private unlocked = false;
  private lastShootAt = 0;

  static readonly STORAGE_KEY = "td_sound_muted";

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    try {
      this.muted = localStorage.getItem(SoundSystem.STORAGE_KEY) === "1";
    } catch {
      // localStorage may be unavailable (privacy mode); default to unmuted.
    }
    const unlock = () => {
      this.ensureCtx();
      if (this.ctx?.state === "suspended") this.ctx.resume();
      this.unlocked = true;
    };
    scene.input.once("pointerdown", unlock);
    scene.input.keyboard?.once("keydown", unlock);
  }

  isMuted(): boolean { return this.muted; }

  setMuted(m: boolean): void {
    this.muted = m;
    try { localStorage.setItem(SoundSystem.STORAGE_KEY, m ? "1" : "0"); } catch { /* ignore */ }
    if (this.masterGain && this.ctx) {
      this.masterGain.gain.setTargetAtTime(m ? 0 : 1.3, this.ctx.currentTime, 0.01);
    }
  }

  toggleMuted(): boolean {
    this.setMuted(!this.muted);
    return this.muted;
  }

  // ───────────────────────────────────────────────────────────────────
  // Public event hooks
  // ───────────────────────────────────────────────────────────────────

  /**
   * Tower fires. Throttled (~55ms) to avoid mass-fire stutter.
   * Accepts (color, towerId) or (towerId).
   */
  onShoot(towerColorOrId: number | string, maybeTowerId?: string): void {
    const now = (this.scene.time?.now ?? performance.now());
    if (now - this.lastShootAt < 55) return;
    this.lastShootAt = now;

    let id: string | undefined;
    let color = 0xffffff;
    if (typeof towerColorOrId === "string") {
      id = towerColorOrId;
    } else {
      color = towerColorOrId;
      id = maybeTowerId;
    }
    const cat: SfxCategory = (id && TOWER_SFX[id]) || this.colorToCategory(color);

    switch (cat) {
      case "fire":      this.sfxFire(); break;
      case "frost":     this.sfxFrost(); break;
      case "bomb":      this.sfxBomb(); break;
      case "lightning": this.sfxLightning(); break;
      case "arcane":    this.sfxArcane(); break;
      case "nature":    this.sfxNature(); break;
      case "laser":     this.sfxLaser(); break;
      case "wind":      this.sfxWind(); break;
      case "dragon":    this.sfxDragon(); break;
      case "storm":     this.sfxStorm(); break;
    }
  }

  onHit(): void {
    // ANIME IMPACT "THWACK" — sharp transient snap + meaty mid-body punch +
    // chest thump + a bright metallic ring. Reads as a real hit landing,
    // not a thin tick. This is the core "feel" sound, so it punches hard.
    this.noiseBurst(0.34, 0.010, 5200, 4);          // crisp snap transient
    this.sweep(1500, 240, 0.09, "square", 0.24);    // punchy down-body
    this.sweep(700, 160, 0.10, "sawtooth", 0.12);   // thicker low body
    this.subBoom(0.32, 0.10, 160, 55);              // meaty chest thump
    this.blip(2700, 0.04, "triangle", 0.10);        // bright steel ring
  }

  onKill(): void {
    // ANIME BURST-POP — a satisfying enemy "pop": downward energy zap +
    // sub thump + bright sparkle shatter + an airy whoosh-out. Replaces the
    // old dull gas hiss with a punchy, rewarding kill confirm.
    this.noiseBurst(0.26, 0.016, 3600, 2);          // burst snap
    this.sweep(950, 110, 0.16, "sawtooth", 0.22);   // energy down-zap
    this.subBoom(0.36, 0.18, 140, 38);              // body pop
    this.crackle(6, 0.24, 3200, 7500, 0.012);       // bright shatter sparkle
    this.noiseSweep(0.14, 0.22, 5200, 1400, 1.0);   // airy dissipation whoosh
  }

  onLeak(): void {
    // Real warning siren — deep air-horn moan + low rumble pad + warning
    // pitch dip. Less synthy: removed the detuned saw stack, used noise
    // bands + a single low body tone.
    this.subBoom(0.40, 1.00, 55, 38);                   // deep body
    this.subBoom(0.30, 0.80, 90, 42);                   // mid kick
    this.noiseBurst(0.20, 0.85, 130, 0.4);              // low rumble pad
    this.noiseSweep(0.16, 0.90, 700, 220, 0.6);         // sustained low moan
    this.noiseSweep(0.12, 0.85, 280, 110, 0.5);         // body of the moan
    // A single low warning note (one tone, no chord) — pitch dips like a foghorn.
    this.sweep(120, 85, 0.85, "triangle", 0.12);
  }

  onWaveStart(): void {
    // Battle commences — metallic SHING (sword unsheathe) → deep impact thud
    // → sustained low Nordic war-horn drone. One unified statement, no
    // melodic fanfare. Feels like "the gates have opened, prepare for war."
    //
    // 1. SWORD UNSHEATHE — high-Q metallic noise sweep (steel scraping out).
    this.noiseSweep(0.22, 0.18, 5500, 1800, 8);
    this.noiseBurst(0.16, 0.04, 4200, 9);            // metallic chime tip
    this.blip(2400, 0.05, "triangle", 0.10);          // bright steel ring
    // 2. IMPACT THUD — heavy sub kick + body crunch (sword hits ground / war signal).
    setTimeout(() => {
      this.subBoom(0.42, 0.55, 70, 28);
      this.noiseBurst(0.22, 0.14, 220, 1.0);          // body crunch
      this.noiseBurst(0.14, 0.05, 1400, 3);           // hit transient
    }, 180);
    // 3. WAR-HORN DRONE — single sustained low Nordic horn, deep + fat.
    //    No pitch motion (no melody). Just one heavy held note.
    setTimeout(() => {
      this.detunedTone(73, 1.10, "sawtooth", 0.26, 12);   // ~D2 root
      this.detunedTone(110, 1.10, "sawtooth", 0.20, -10); // fifth-ish layer above
      this.detunedTone(146, 0.95, "sawtooth", 0.14, 8);   // octave-up body
      this.subBoom(0.30, 1.00, 55, 36);                    // sustained sub bed
      this.noiseSweep(0.10, 1.10, 320, 180, 0.6);          // breathy horn air
    }, 320);
  }

  onWaveClear(): void {
    // Realistic victory bell + shimmer wash. Single church-bell ring with
    // its natural overtone, plus a noise-based crowd-shimmer. No detuned
    // saw chord, no synth pad.
    this.blip(660, 0.05, "triangle", 0.18);                    // bell strike
    this.detunedTone(660, 0.85, "sine", 0.18, 2);              // bell fundamental
    this.detunedTone(1320, 0.60, "sine", 0.10, 3);             // octave overtone
    setTimeout(() => this.blip(990, 0.70, "sine", 0.10), 90);  // 5th harmonic shimmer
    this.noiseSweep(0.14, 0.70, 5500, 8500, 2);                // bright shimmer wash
    this.crackle(8, 0.70, 4500, 9000, 0.02);                    // sparkle
    this.subBoom(0.18, 0.45, 75, 32);                            // soft body
  }

  onPlace(): void {
    // Heavy anvil-on-stone — metal clang + stone crunch + sub thud.
    this.noiseBurst(0.30, 0.10, 380, 2.8);
    this.sweep(420, 140, 0.12, "triangle", 0.22);
    this.noiseBurst(0.16, 0.20, 180, 1.0);
    this.subBoom(0.30, 0.22, 100, 38);
  }

  onClick(): void {
    // UI tick — soft noise click + bright square blip.
    this.noiseBurst(0.08, 0.025, 4500, 8);
    this.blip(820, 0.025, "square", 0.10);
  }

  onUpgrade(): void {
    // Rising shimmer + chord stack — premium upgrade flourish.
    this.sweep(440, 1320, 0.30, "sawtooth", 0.16);
    this.detunedTone(660, 0.32, "sawtooth", 0.14, 4);
    this.detunedTone(990, 0.28, "sawtooth", 0.10, 5);
    this.detunedTone(1320, 0.24, "triangle", 0.08, 3);
    this.noiseBurst(0.14, 0.040, 5200, 4);
    this.subBoom(0.18, 0.20, 90, 40);
  }

  onSell(): void {
    // Premium coin clink — 3 bright triangle plinks.
    this.blip(1400, 0.10, "triangle", 0.18);
    setTimeout(() => this.blip(940, 0.12, "triangle", 0.14), 80);
    setTimeout(() => this.blip(660, 0.14, "triangle", 0.10), 170);
  }

  onVictory(): void {
    // Triumph — bell strike + shimmer wash + crowd cheer.
    // Stage 1: bell strike.
    this.blip(880, 0.06, "triangle", 0.20);
    this.detunedTone(880, 1.00, "sine", 0.20, 3);
    this.detunedTone(1320, 0.80, "sine", 0.14, 4);
    this.subBoom(0.30, 0.50, 95, 38);
    // Stage 2: shimmer wash (~250ms in).
    setTimeout(() => {
      this.noiseSweep(0.18, 0.90, 5500, 9500, 2);
      this.detunedTone(1760, 0.70, "sine", 0.10, 6);
      this.detunedTone(2640, 0.50, "sine", 0.06, 7);
    }, 250);
    // Stage 3: crowd cheer pad (~600ms in).
    setTimeout(() => {
      this.detunedTone(440, 0.90, "sawtooth", 0.14, 12);
      this.detunedTone(660, 0.90, "sawtooth", 0.12, 10);
      this.detunedTone(880, 0.90, "sawtooth", 0.10, 8);
      this.noiseSweep(0.12, 0.80, 1800, 3500, 1);
    }, 600);
  }

  onDefeat(): void {
    // Funeral toll — sub boom + low rumble + descending triangle.
    this.subBoom(0.46, 1.20, 80, 22);
    this.noiseBurst(0.20, 1.00, 140, 0.4);
    this.sweep(330, 110, 1.10, "triangle", 0.18);
    this.detunedTone(165, 1.10, "sawtooth", 0.20, 10);
    setTimeout(() => {
      this.sweep(220, 80, 1.30, "triangle", 0.14);
      this.subBoom(0.28, 1.10, 50, 18);
    }, 700);
  }

  // ───────────────────────────────────────────────────────────────────
  // Tower SFX profiles — cinematic, multi-layer, world-class
  // ───────────────────────────────────────────────────────────────────

  /**
   * Fire — Lina Dragon Slave: red fire wave with sawtooth fire-roar carrier.
   * Snap + descending fire ring + body sweep + ember pops + sub + afterglow.
   */
  private sfxFire(): void {
    // KI FIREBALL — rising charge whoosh → ignition crack → roaring fire
    // release with a fierce sawtooth body and ember crackle. Anime energy blast.
    this.noiseSweep(0.16, 0.09, 400, 2000, 1.2);     // rising charge whoosh
    this.noiseBurst(0.34, 0.016, 2400, 2.0);          // ignition crack
    this.noiseBurst(0.22, 0.014, 6000, 4.0);          // bright spark tip
    this.sweep(1900, 380, 0.26, "sawtooth", 0.22);    // fire-roar release
    this.sweep(950, 200, 0.22, "square", 0.13);       // thick body
    this.noiseSweep(0.20, 0.38, 1300, 320, 0.6);      // roaring flame body
    this.crackle(7, 0.34, 800, 3800, 0.022);          // embers
    this.subBoom(0.26, 0.22, 130, 42);                // launch thump
  }

  /**
   * Frost — Rylai Crystal Maiden Frostbite TEMPLATE: glassy descending
   * triangle ring + ice-shard ticks. Snap → ring-down → body → ticks → sub.
   */
  private sfxFrost(): void {
    // FROST CRYSTAL SHOT — a quick icy charge shimmer → sharp glassy SNAP →
    // crystalline ring-down + shard ticks. Crisp and magical.
    this.noiseSweep(0.12, 0.08, 6000, 9000, 2.0);     // icy charge shimmer-up
    this.noiseBurst(0.30, 0.016, 6000, 4.0);          // glassy SNAP
    this.sweep(2800, 620, 0.30, "triangle", 0.20);    // descending ice ring
    this.sweep(5200, 1300, 0.24, "triangle", 0.11);   // shimmer harmonic
    this.noiseSweep(0.15, 0.32, 3800, 1000, 0.6);     // frosty air body
    this.crackle(10, 0.34, 5000, 9500, 0.012);        // ice shard ticks
    this.subBoom(0.18, 0.16, 90, 36);                  // sub plunge
    setTimeout(() => this.blip(1480, 0.14, "triangle", 0.08), 90);
  }

  /**
   * Bomb — Boush Heavy Artillery: MASSIVE detonation. Big aggressive
   * overpressure crack + huge sawtooth shockwave + multi-stage sub stack
   * + heavy debris rain + delayed echo + long tail rumble.
   */
  private sfxBomb(): void {
    // 0. INCOMING whistle — falling-pitch shell scream just before impact.
    this.sweep(1800, 600, 0.09, "triangle", 0.14);
    // 1. Overpressure crack — extra loud broadband transient stack.
    this.noiseBurst(0.85, 0.014, 2200, 0.8);          // hard SNAP
    this.noiseBurst(0.70, 0.022, 900, 0.6);           // body of snap
    this.noiseBurst(0.55, 0.040, 280, 0.4);           // low thump
    // 2. Shockwave-tone — aggressive sawtooth pitch-drop, two octaves.
    this.sweep(620, 90, 0.55, "sawtooth", 0.34);      // upper shockwave
    this.sweep(320, 55, 0.65, "sawtooth", 0.26);      // mid shockwave
    this.sweep(180, 45, 0.75, "sawtooth", 0.20);      // deep shock body
    // 3. Pressure-wave body + dust cloud rumble.
    this.noiseSweep(0.50, 0.65, 1200, 220, 0.5);
    this.noiseSweep(0.40, 0.75, 500, 110, 0.4);
    // 4. Massive sub stack — quad-kick punch you feel in the chest.
    this.subBoom(0.95, 0.55, 110, 24);                 // sub kick 1
    this.subBoom(0.80, 0.65, 70, 22);                  // sub kick 2
    this.subBoom(0.60, 0.75, 45, 20);                  // sub kick 3
    // 5. Debris rain — many low/mid pops scattered.
    this.crackle(26, 0.90, 150, 2000, 0.045);
    this.crackle(14, 1.00, 60, 500, 0.070);
    // 6. Echo bouncing back from distant terrain (~320ms).
    setTimeout(() => {
      this.noiseBurst(0.46, 0.030, 380, 0.4);
      this.subBoom(0.50, 0.75, 60, 22);
      this.noiseSweep(0.32, 0.85, 400, 80, 0.4);
    }, 320);
    // 7. Distant final rumble.
    setTimeout(() => this.subBoom(0.28, 0.95, 40, 18), 850);
  }

  /**
   * Laser — Star Wars / arcade-style "PEW". The textbook sci-fi blaster:
   * ONE sharp descending square-wave carrier (3.5kHz → 250Hz over 90ms),
   * tiny noise click at the head for crispness, and a brief sub thump.
   * No FM buzz, no plasma sizzle — those muddy the classic pew.
   */
  private sfxLaser(): void {
    // ANIME ENERGY BEAM — a brief plasma charge whine → searing "PYEW" beam
    // release with an FM-buzz core + plasma sizzle tail + chest thump. Hot.
    this.noiseSweep(0.12, 0.06, 2000, 6000, 3.0);     // plasma charge whine
    this.noiseBurst(0.18, 0.005, 8000, 8);            // muzzle snap
    this.sweep(3800, 230, 0.10, "square", 0.32);      // THE PYEW
    this.fmZap(1900, 700, 240, 0.10, 0.16);           // buzzing beam core
    this.sweep(1750, 130, 0.11, "sawtooth", 0.16);    // thick low body
    this.noiseSweep(0.12, 0.16, 6000, 2200, 4.0);     // plasma sizzle tail
    this.subBoom(0.18, 0.09, 95, 38);                 // chest thump
  }

  /**
   * Wind — descending triangle air-tone + body + whistle.
   */
  private sfxWind(): void {
    // WIND SLASH — a fast cutting whoosh-up → sharp air-blade release with a
    // whistling overtone and a trailing gust. Quick and slicing.
    this.noiseSweep(0.20, 0.12, 800, 3200, 1.2);      // cutting whoosh-up
    this.noiseBurst(0.20, 0.014, 3600, 4);            // air-blade crack
    this.sweep(1400, 320, 0.30, "triangle", 0.16);    // air-tone ring
    this.sweep(2600, 680, 0.26, "triangle", 0.10);    // whistle overtone
    this.noiseSweep(0.18, 0.38, 2400, 500, 0.6);      // trailing gust
    setTimeout(() => this.blip(980, 0.16, "triangle", 0.08), 80);
  }

  /**
   * Dragon — Jakiro Dual Breath: sawtooth throat growl + breath body + sub.
   */
  private sfxDragon(): void {
    // DRAGON BREATH ROAR — a deep throat inhale → guttural growl release with
    // a roaring breath body, hot ember crackle and a heavy chest sub. Beastly.
    this.noiseSweep(0.16, 0.10, 300, 900, 0.6);       // throat inhale
    this.noiseBurst(0.26, 0.026, 1300, 2.0);          // breath ignition
    this.sweep(680, 200, 0.42, "sawtooth", 0.24);     // throat growl
    this.sweep(1500, 460, 0.34, "sawtooth", 0.14);    // upper growl
    this.noiseSweep(0.26, 0.50, 1800, 480, 0.6);      // roaring breath body
    this.noiseSweep(0.17, 0.48, 600, 180, 0.5);       // hot air low
    this.crackle(8, 0.42, 1200, 5000, 0.020);         // ember pops
    this.subBoom(0.32, 0.50, 120, 30);                // chest sub
  }

  /**
   * Storm — Vrogros howling wind + sawtooth storm voice + thunder crack.
   */
  private sfxStorm(): void {
    // STORM SURGE — howling wind wall with a buzzing storm-voice + a violent
    // thunder crack mid-blast. Bigger and more menacing now.
    this.noiseSweep(0.30, 0.95, 3600, 1000, 1.0);
    this.noiseSweep(0.24, 1.00, 1100, 340, 0.6);
    this.noiseSweep(0.15, 1.00, 320, 100, 0.5);
    this.fmZap(420, 200, 40, 0.30, 0.10);             // buzzing storm voice
    this.sweep(700, 170, 0.85, "sawtooth", 0.16);     // storm wail
    this.sweep(330, 85, 1.00, "sawtooth", 0.10);      // low storm body
    this.crackle(28, 1.00, 2200, 7000, 0.010);        // rain droplets
    setTimeout(() => {
      this.noiseBurst(0.42, 0.018, 5500, 6);
      this.noiseBurst(0.32, 0.030, 1400, 2);
      this.subBoom(0.48, 0.80, 85, 22);
      this.subBoom(0.32, 0.70, 50, 20);
      this.sweep(180, 60, 0.85, "sawtooth", 0.12);
    }, 280);
    setTimeout(() => this.subBoom(0.20, 0.80, 40, 18), 950);
  }

  /**
   * Lightning — Zeus arc-zap: crisp, snappy electric whip-crack. One sharp
   * ionizing snap + descending square arc + brief ozone tick + single tight
   * sub punch. Short and surgical (no rolling-thunder tail).
   */
  private sfxLightning(): void {
    // ANIME THUNDER ZAP — a crackling electric charge → blinding arc-crack →
    // FM-buzzing bolt + ozone sizzle + tight thunder thump. Sharp and violent.
    this.crackle(4, 0.07, 6000, 9500, 0.006);          // pre-charge crackle
    this.noiseBurst(0.40, 0.007, 9000, 6);             // blinding arc tick
    this.noiseBurst(0.28, 0.012, 3400, 3);             // snap body
    this.fmZap(2600, 1400, 320, 0.11, 0.24);           // buzzing electric bolt
    this.sweep(4400, 480, 0.10, "square", 0.26);       // main bolt down-arc
    this.sweep(2200, 240, 0.11, "sawtooth", 0.14);     // lower harmonic
    this.noiseSweep(0.16, 0.11, 7000, 3000, 5);        // ozone sizzle
    this.subBoom(0.38, 0.16, 100, 36);                 // thunder thump
  }

  /**
   * Arcane — ethereal descending triangle + soft chime + void body.
   */
  private sfxArcane(): void {
    // VOID ENERGY BLAST — a dark warbling charge → ethereal release with a
    // detuned void chord + descending shimmer + deep void sub. Ominous power.
    this.fmZap(520, 240, 60, 0.14, 0.12);             // dark warbling charge
    this.noiseBurst(0.22, 0.014, 5000, 5);            // cast snap
    this.sweep(2000, 600, 0.36, "triangle", 0.19);    // void release ring
    this.sweep(3800, 1200, 0.30, "triangle", 0.11);   // upper shimmer
    this.detunedTone(880, 0.40, "sawtooth", 0.13, 8); // void chord (gritty)
    this.detunedTone(1320, 0.30, "sawtooth", 0.09, 9);
    this.noiseSweep(0.15, 0.40, 2000, 500, 0.8);      // void rush body
    this.subBoom(0.20, 0.30, 60, 28);                  // deep void sub
  }

  /**
   * Nature — Mirana Sacred Arrow: bow-string snap + descending bow-ring
   * triangle + moon ticks + moon-ring tail.
   */
  private sfxNature(): void {
    // SACRED ARROW — taut bow draw → sharp string THWANG → screaming arrow
    // whoosh with a moon-energy shimmer trail. Fast and piercing.
    this.noiseSweep(0.12, 0.07, 600, 1600, 2.0);       // bow draw creak
    this.noiseBurst(0.30, 0.012, 2000, 5);             // string THWANG
    this.sweep(1300, 340, 0.26, "triangle", 0.22);     // bow ring-down
    this.sweep(2600, 720, 0.22, "triangle", 0.11);     // overtone shimmer
    this.noiseSweep(0.20, 0.24, 3000, 600, 4.0);       // screaming arrow whoosh
    this.crackle(6, 0.26, 3500, 7000, 0.010);          // moon-energy ticks
    this.subBoom(0.16, 0.13, 90, 38);
  }
  /** Fallback: derive a category from the tower's display color. */
  private colorToCategory(color: number): SfxCategory {
    const r = (color >> 16) & 0xff;
    const g = (color >> 8) & 0xff;
    const b = color & 0xff;
    if (r > 180 && g < 130 && b < 130) return "fire";
    if (b > 180 && r < 160) return "frost";
    if (g > 180 && r < 180) return "nature";
    if (r > 150 && g > 130 && b < 110) return "bomb";
    if (r > 200 && g > 200 && b > 200) return "lightning";
    return "arcane";
  }

  // ───────────────────────────────────────────────────────────────────
  // Low-level synth helpers
  // ───────────────────────────────────────────────────────────────────

  private ensureCtx(): boolean {
    if (this.ctx) return true;
    try {
      const Ctor = (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext);
      if (!Ctor) return false;
      this.ctx = new Ctor();

      // Master compressor — punchy "loudness war" glue: lower threshold +
      // higher ratio so every hit slams forward and the mix feels energetic.
      this.compressor = this.ctx.createDynamicsCompressor();
      this.compressor.threshold.value = -20;
      this.compressor.knee.value = 8;
      this.compressor.ratio.value = 6;
      this.compressor.attack.value = 0.003;
      this.compressor.release.value = 0.14;

      // Master tone — keep just a touch of high-shelf taming, but stay bright
      // and crisp so shots/impacts have snap and excitement (was dull before).
      this.masterTilt = this.ctx.createBiquadFilter();
      this.masterTilt.type = "highshelf";
      this.masterTilt.frequency.value = 4200;
      this.masterTilt.gain.value = -1.5;          // barely tamed top end

      this.masterLP = this.ctx.createBiquadFilter();
      this.masterLP.type = "lowpass";
      this.masterLP.frequency.value = 12000;      // wide-open, snappy treble
      this.masterLP.Q.value = 0.5;

      // Master gain (mute control sits here) — pushed hot for impact.
      this.masterGain = this.ctx.createGain();
      this.masterGain.gain.value = this.muted ? 0 : 1.3;
      this.compressor.connect(this.masterTilt);
      this.masterTilt.connect(this.masterLP);
      this.masterLP.connect(this.masterGain);
      this.masterGain.connect(this.ctx.destination);

      // Feedback-delay reverb send for cinematic tail.
      this.reverbSend = this.ctx.createGain();
      this.reverbSend.gain.value = 0.30;
      const delay = this.ctx.createDelay(1.5);
      delay.delayTime.value = 0.105;
      const fb = this.ctx.createGain();
      fb.gain.value = 0.42;
      const lp = this.ctx.createBiquadFilter();
      lp.type = "lowpass";
      lp.frequency.value = 4200;
      const reverbReturn = this.ctx.createGain();
      reverbReturn.gain.value = 0.65;
      this.reverbSend.connect(delay);
      delay.connect(lp);
      lp.connect(fb);
      fb.connect(delay);
      lp.connect(reverbReturn);
      reverbReturn.connect(this.compressor);
      return true;
    } catch {
      return false;
    }
  }

  /** Connect a layer's gain node to the master chain (compressor) + reverb send. */
  private routeGain(g: GainNode): void {
    if (!this.compressor || !this.reverbSend) return;
    g.connect(this.compressor);
    g.connect(this.reverbSend);
  }

  /** One-shot tonal blip with quick attack / exp decay envelope. */
  private blip(freq: number, durSec: number, type: OscillatorType, volume: number): void {
    if (this.muted) return;
    if (!this.ensureCtx() || !this.ctx) return;
    const ctx = this.ctx;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, now);
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(volume, now + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, now + durSec);
    osc.connect(g);
    this.routeGain(g);
    osc.start(now);
    osc.stop(now + durSec + 0.02);
  }

  private blipDelayed(freq: number, durSec: number, type: OscillatorType, volume: number, delayMs: number): void {
    setTimeout(() => this.blip(freq, durSec, type, volume), delayMs);
  }

  /** Pitch-sweep tone — exponential glide from f0 → f1. */
  private sweep(f0: number, f1: number, durSec: number, type: OscillatorType, volume: number): void {
    if (this.muted) return;
    if (!this.ensureCtx() || !this.ctx) return;
    const ctx = this.ctx;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(f0, now);
    osc.frequency.exponentialRampToValueAtTime(Math.max(20, f1), now + durSec);
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(volume, now + 0.006);
    g.gain.exponentialRampToValueAtTime(0.0001, now + durSec);
    osc.connect(g);
    this.routeGain(g);
    osc.start(now);
    osc.stop(now + durSec + 0.02);
  }

  /** Detuned dual-osc tone (fat sound) — used for horns / chord stacks / pads. */
  private detunedTone(freq: number, durSec: number, type: OscillatorType, volume: number, detuneCents: number): void {
    if (this.muted) return;
    if (!this.ensureCtx() || !this.ctx) return;
    const ctx = this.ctx;
    const now = ctx.currentTime;
    const a = ctx.createOscillator();
    const b = ctx.createOscillator();
    a.type = type;
    b.type = type;
    a.frequency.setValueAtTime(freq, now);
    b.frequency.setValueAtTime(freq, now);
    a.detune.setValueAtTime(-detuneCents, now);
    b.detune.setValueAtTime(detuneCents, now);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(volume, now + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, now + durSec);
    a.connect(g); b.connect(g);
    this.routeGain(g);
    a.start(now); b.start(now);
    a.stop(now + durSec + 0.02);
    b.stop(now + durSec + 0.02);
  }

  /** FM-modulated zap — carrier square + LFO modulator on frequency. */
  private fmZap(carrierFreq: number, modDepth: number, modRate: number, durSec: number, volume: number): void {
    if (this.muted) return;
    if (!this.ensureCtx() || !this.ctx) return;
    const ctx = this.ctx;
    const now = ctx.currentTime;
    const carrier = ctx.createOscillator();
    carrier.type = "square";
    carrier.frequency.setValueAtTime(carrierFreq, now);
    const mod = ctx.createOscillator();
    mod.type = "sawtooth";
    mod.frequency.setValueAtTime(modRate, now);
    const modGain = ctx.createGain();
    modGain.gain.setValueAtTime(modDepth, now);
    mod.connect(modGain);
    modGain.connect(carrier.frequency);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(volume, now + 0.004);
    g.gain.exponentialRampToValueAtTime(0.0001, now + durSec);
    carrier.connect(g);
    this.routeGain(g);
    carrier.start(now); mod.start(now);
    carrier.stop(now + durSec + 0.02); mod.stop(now + durSec + 0.02);
  }

  /** Bandpassed noise burst — used for sizzles, crunches, spark cracks. */
  private noiseBurst(volume: number, durSec: number, centerHz: number, qFactor: number): void {
    if (this.muted) return;
    if (!this.ensureCtx() || !this.ctx) return;
    const ctx = this.ctx;
    const now = ctx.currentTime;
    const len = Math.max(1, Math.floor(ctx.sampleRate * (durSec + 0.05)));
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const filter = ctx.createBiquadFilter();
    filter.type = "bandpass";
    filter.frequency.setValueAtTime(centerHz, now);
    filter.Q.setValueAtTime(qFactor, now);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(volume, now + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, now + durSec);
    src.connect(filter); filter.connect(g);
    this.routeGain(g);
    src.start(now);
    src.stop(now + durSec + 0.05);
  }

  /** Bandpass noise with sweeping center frequency (fire sizzle, wave roar, dust hiss). */
  private noiseSweep(volume: number, durSec: number, centerStartHz: number, centerEndHz: number, qFactor: number): void {
    if (this.muted) return;
    if (!this.ensureCtx() || !this.ctx) return;
    const ctx = this.ctx;
    const now = ctx.currentTime;
    const len = Math.max(1, Math.floor(ctx.sampleRate * (durSec + 0.05)));
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const filter = ctx.createBiquadFilter();
    filter.type = "bandpass";
    filter.frequency.setValueAtTime(centerStartHz, now);
    filter.frequency.exponentialRampToValueAtTime(Math.max(40, centerEndHz), now + durSec);
    filter.Q.setValueAtTime(qFactor, now);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(volume, now + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, now + durSec);
    src.connect(filter); filter.connect(g);
    this.routeGain(g);
    src.start(now);
    src.stop(now + durSec + 0.05);
  }

  /** Sub-bass boom — sine kick with pitch drop + lowpass body. */
  private subBoom(volume: number, durSec: number, startHz: number, endHz: number): void {
    if (this.muted) return;
    if (!this.ensureCtx() || !this.ctx) return;
    const ctx = this.ctx;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(startHz, now);
    osc.frequency.exponentialRampToValueAtTime(Math.max(20, endHz), now + durSec);
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 220;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(volume, now + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, now + durSec);
    osc.connect(lp); lp.connect(g);
    if (this.compressor) g.connect(this.compressor);
    osc.start(now);
    osc.stop(now + durSec + 0.02);
  }

  /**
   * Crackle — N random bandpass-noise pops scattered across durSec.
   * Used for ember crackle, debris fall, foam pops, electric sparks.
   */
  private crackle(count: number, durSec: number, fLow: number, fHigh: number, popDurSec: number): void {
    for (let i = 0; i < count; i++) {
      const delayMs = Math.random() * durSec * 1000;
      const freq = fLow + Math.random() * (fHigh - fLow);
      const vol = 0.10 + Math.random() * 0.10;
      const q = 4 + Math.random() * 6;
      setTimeout(() => this.noiseBurst(vol, popDurSec, freq, q), delayMs);
    }
  }
}
